/**
 * Receipt Printer Utility
 * Provides functions for printing receipts to thermal printers, generating PDFs, and managing print queues
 */

interface ReceiptPrintOptions {
  autoClose?: boolean;
  showPreview?: boolean;
  printerName?: string;
  paperWidth?: number; // in mm, default 80mm
}

interface PrintResult {
  success: boolean;
  error?: string;
}

/**
 * Utility to print content via a hidden iframe
 */
export const printViaIframe = (
  contentHtml: string,
  pageStyle: string,
  autoClose = true,
  title = 'Receipt'
): Promise<PrintResult> => {
  return new Promise((resolve) => {
    try {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      iframe.setAttribute('title', title);
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) {
        document.body.removeChild(iframe);
        resolve({ success: false, error: 'Cannot access iframe document' });
        return;
      }

      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>${title}</title>
            <style>
              ${pageStyle}
            </style>
          </head>
          <body>
            ${contentHtml}
          </body>
        </html>
      `);
      doc.close();

      const win = iframe.contentWindow;
      if (!win) {
        document.body.removeChild(iframe);
        resolve({ success: false, error: 'Cannot access iframe window' });
        return;
      }

      const cleanup = () => {
        setTimeout(() => {
          try {
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          } catch (e) {}
          resolve({ success: true });
        }, 800);
      };

      setTimeout(() => {
        try {
          win.focus();
          win.onafterprint = cleanup;
          win.print();
          setTimeout(cleanup, 4000);
        } catch (err: any) {
          console.error('Print execution failed:', err);
          cleanup();
        }
      }, 300);
    } catch (err: any) {
      resolve({ success: false, error: err?.message || 'Print error' });
    }
  });
};

/**
 * Print receipt to thermal printer or default printer
 * @param receiptElement - React ref to the receipt component
 * @param options - Print configuration options
 */
export const printReceipt = async (
  receiptElement: HTMLElement | null,
  options: ReceiptPrintOptions = {}
): Promise<PrintResult> => {
  if (!receiptElement) {
    return { success: false, error: 'Receipt element not found' };
  }

  const {
    autoClose = true,
    paperWidth = 80,
  } = options;

  const receiptStyles = `
    @page {
      size: ${paperWidth}mm auto;
      margin: 0;
    }
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      visibility: visible !important;
    }
    
    body {
      margin: 0;
      padding: 10px;
      font-family: 'Courier New', Consolas, monospace;
      font-size: 12px;
      line-height: 1.4;
      color: #000;
      background: #fff;
      visibility: visible !important;
    }

    .professional-receipt {
      max-width: ${paperWidth}mm;
      margin: 0 auto;
      padding: 10px;
    }

    .receipt-header {
      text-align: center;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 2px dashed #000;
    }

    .receipt-logo-img, .receipt-logo img {
      max-width: 56px !important;
      max-height: 44px !important;
      width: auto !important;
      height: auto !important;
      object-fit: contain !important;
      margin: 0 auto 6px auto !important;
      display: block !important;
    }

    .logo-placeholder {
      width: 50px;
      height: 50px;
      margin: 0 auto 8px;
      background: #022c22;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .logo-text {
      font-size: 24px;
      font-weight: bold;
      color: #fff;
    }

    .receipt-shop-name {
      font-size: 18px;
      font-weight: bold;
      margin: 8px 0;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .receipt-contact-info {
      font-size: 10px;
      color: #333;
    }

    .receipt-contact-info p {
      margin: 2px 0;
    }

    .receipt-divider, hr {
      margin: 10px 0;
      border: none;
      border-top: 1px dashed #000;
    }

    .receipt-divider-bold {
      border-top: 2px solid #000;
    }

    .receipt-transaction-info {
      margin-bottom: 12px;
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 4px;
      font-size: 11px;
    }

    .info-label { color: #666; }
    .info-value { font-weight: bold; }

    .items-header {
      display: grid;
      grid-template-columns: 2.5fr 0.8fr 1.3fr 1.4fr;
      gap: 4px;
      font-weight: bold;
      font-size: 10px;
      padding: 6px 0;
      border-top: 1px solid #000;
      border-bottom: 1px solid #000;
      text-transform: uppercase;
    }

    .item-row {
      display: grid;
      grid-template-columns: 2.5fr 0.8fr 1.3fr 1.4fr;
      gap: 4px;
      font-size: 11px;
      padding: 6px 0;
      border-bottom: 1px dotted #ccc;
    }

    .item-name { text-align: left; font-weight: 500; }
    .item-qty { text-align: center; }
    .item-price { text-align: right; }
    .item-total { text-align: right; font-weight: bold; }

    .loan-badge {
      color: #dc2626;
      font-weight: bold;
      font-size: 9px;
    }

    .unit-name {
      font-size: 9px;
      color: #666;
      display: block;
    }

    .receipt-summary {
      margin: 12px 0;
    }

    .summary-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 4px;
      font-size: 12px;
    }

    .summary-value { font-weight: bold; }

    .summary-row-total {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 2px solid #000;
      font-size: 16px;
      font-weight: bold;
    }

    .summary-label-total { text-transform: uppercase; }
    .summary-value-total { font-size: 18px; }

    .summary-row-loan { color: #dc2626; }

    .summary-stats {
      margin-top: 8px;
      font-size: 10px;
      color: #666;
    }

    .stat-item {
      display: flex;
      justify-content: space-between;
      margin-bottom: 2px;
    }

    .receipt-footer {
      text-align: center;
      margin-top: 16px;
      padding-top: 12px;
      border-top: 2px dashed #000;
    }

    .thank-you-message {
      font-size: 13px;
      font-weight: bold;
      margin-bottom: 8px;
      text-transform: uppercase;
    }

    .return-policy {
      font-size: 9px;
      color: #666;
      margin-bottom: 4px;
    }

    .footer-note {
      font-size: 9px;
      color: #999;
      font-style: italic;
      margin-top: 8px;
    }

    @media print {
      body { margin: 0; padding: 5mm; }
      .no-print { display: none !important; }
    }
  `;

  return printViaIframe(receiptElement.innerHTML, receiptStyles, autoClose, 'Receipt');
};

/**
 * Print receipt as a high-quality image (Raster mode)
 * This is the most reliable way to print from browsers to thermal printers
 * as it avoids PostScript/PDF rendering issues on macOS.
 * 
 * @param receiptElement - React ref to the receipt component
 * @param options - Print configuration options
 */
export const printReceiptAsImage = async (
  receiptElement: HTMLElement | null,
  options: ReceiptPrintOptions = {}
): Promise<PrintResult> => {
  if (!receiptElement) {
    return { success: false, error: 'Receipt element not found' };
  }

  const html2canvas = (await import('html2canvas')).default;

  const { autoClose = true, paperWidth = 80 } = options;

  try {
    // ── Safe printable width ────────────────────────────────────────────────
    // Using hardware-accurate values for 80mm and 58mm paper
    const is80mm = paperWidth === 80 || paperWidth === 72;
    const SAFE_MARGIN_MM = 4; // High safety margin
    const contentWidthMm  = paperWidth - SAFE_MARGIN_MM * 2;
    const captureWidthPx  = is80mm ? 300 : 200; // Wide Gutter Strategy (Capture > Template)

    // ── Rasterise ───────────────────────────────────────────────────────────
    const canvas = await html2canvas(receiptElement, {
      scale: 4,          // High resolution for text
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: captureWidthPx,
      scrollX: 0,
      scrollY: 0,
      onclone: (_doc, clonedEl) => {
        // Honor the padding set in the component for safety buffers
        clonedEl.style.position = 'relative';
        clonedEl.style.left     = '0';
        clonedEl.style.top      = '0';
        clonedEl.style.margin   = '0';  
        clonedEl.style.width    = `${captureWidthPx}px`;
        clonedEl.style.minWidth = `${captureWidthPx}px`;
        clonedEl.style.maxWidth = `${captureWidthPx}px`;
        clonedEl.style.visibility = 'visible';
        clonedEl.style.opacity  = '1';

        // Constrain any logo image strictly so large graphics don't blow up
        const logoImgs = clonedEl.querySelectorAll<HTMLImageElement>('.receipt-logo-img, .receipt-logo img, img');
        logoImgs.forEach((img) => {
          if (!img.classList.contains('ant-qrcode-img')) {
            img.style.maxWidth = '56px';
            img.style.maxHeight = '44px';
            img.style.width = 'auto';
            img.style.height = 'auto';
            img.style.objectFit = 'contain';
            img.style.margin = '0 auto 6px auto';
            img.style.display = 'block';
          }
        });
      },
    });

    const imgData = canvas.toDataURL('image/png');

    const receiptContent = `
      <div class="img-container">
        <img src="${imgData}" alt="Receipt" />
      </div>
    `;

    const receiptStyle = `
      @page {
        size: ${paperWidth}mm auto;
        margin: 0 !important;
      }
      * { margin: 0; padding: 0; box-sizing: border-box; visibility: visible !important; }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        width: 100% !important;
        height: auto !important;
        max-height: fit-content !important;
        overflow: hidden !important;
        visibility: visible !important;
      }
      .img-container {
        padding: 0 0 0 2mm;
        width: 100%;
        display: flex;
        justify-content: flex-start; 
        align-items: flex-start;
      }
      img {
        display: block;
        width: ${contentWidthMm}mm !important;
        height: auto;
        image-rendering: -webkit-optimize-contrast;
      }
      @media print {
        body { margin: 0 !important; padding: 0 !important; }
        .no-print { display: none !important; }
      }
    `;

    return await printViaIframe(receiptContent, receiptStyle, autoClose, 'Receipt');
  } catch (error: any) {
    console.error('Image Print error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get the correct off-screen container width (in px) for a given paper size.
 */
export const getThermalCaptureWidth = (paperWidthMm: number): number => {
  return paperWidthMm >= 80 ? 300 : 200;
};

/**
 * Generate and download PDF from receipt
 * @param receiptElement - React ref to the receipt component
 * @param filename - PDF filename
 */
export const generateReceiptPDF = async (
  receiptElement: HTMLElement | null,
  filename = 'receipt.pdf'
): Promise<PrintResult> => {
  if (!receiptElement) {
    return { success: false, error: 'Receipt element not found' };
  }

  try {
    return await printReceiptAsImage(receiptElement, {
      paperWidth: 80,
      autoClose: true,
    });
  } catch (error: any) {
    console.error('PDF generation error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send receipt to thermal printer using ESC/POS commands (placeholder)
 * This would require a backend service or browser extension for actual thermal printer support
 */
export const sendToThermalPrinter = async (
  receiptData: any,
  printerIP?: string
): Promise<PrintResult> => {
  console.warn('Thermal printer integration requires backend service or browser extension');
  
  // Placeholder for future implementation
  // Would send ESC/POS commands to printer via:
  // - WebSocket connection to local print server
  // - Browser extension
  // - Electron app integration
  
  return {
    success: false,
    error: 'Thermal printer integration not yet implemented. Use regular print instead.',
  };
};

/**
 * Print queue manager for handling multiple receipts
 */
class PrintQueue {
  private queue: Array<() => Promise<PrintResult>> = [];
  private isProcessing = false;

  /**
   * Add print job to queue
   */
  add(printJob: () => Promise<PrintResult>): void {
    this.queue.push(printJob);
    if (!this.isProcessing) {
      this.process();
    }
  }

  /**
   * Process print queue
   */
  private async process(): Promise<void> {
    if (this.queue.length === 0) {
      this.isProcessing = false;
      return;
    }

    this.isProcessing = true;
    const job = this.queue.shift();

    if (job) {
      try {
        await job();
        // Wait 1 second between print jobs
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Print job failed:', error);
      }
    }

    this.process();
  }

  /**
   * Clear all pending jobs
   */
  clear(): void {
    this.queue = [];
    this.isProcessing = false;
  }

  /**
   * Get queue length
   */
  get length(): number {
    return this.queue.length;
  }
}

// Export singleton print queue
export const printQueue = new PrintQueue();

/**
 * Configure printer settings (stored in localStorage)
 */
export interface PrinterSettings {
  defaultPrinter: string;
  paperWidth: number;
  paperSize: string;
  autoClose: boolean;
  showPreview: boolean;
  autoPrint: boolean;
}

const SETTINGS_KEY = 'fuko_printer_settings';

export const getPrinterSettings = (backendSettings?: Partial<PrinterSettings>): PrinterSettings => {
  // Prefer backend settings if provided
  if (backendSettings && Object.keys(backendSettings).length > 0) {
    return {
      defaultPrinter: backendSettings.defaultPrinter || 'default',
      paperWidth: backendSettings.paperWidth || 80,
      paperSize: backendSettings.paperSize || 'A4',
      autoClose: backendSettings.autoClose !== undefined ? backendSettings.autoClose : true,
      showPreview: backendSettings.showPreview !== undefined ? backendSettings.showPreview : false,
      autoPrint: backendSettings.autoPrint !== undefined ? backendSettings.autoPrint : false,
    };
  }

  const stored = localStorage.getItem(SETTINGS_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse printer settings:', e);
    }
  }

  // Default settings
  return {
    defaultPrinter: 'default',
    paperWidth: 80,
    paperSize: 'A4',
    autoClose: true,
    showPreview: false,
    autoPrint: false,
  };
};

export const savePrinterSettings = (settings: Partial<PrinterSettings>): void => {
  const current = getPrinterSettings();
  const updated = { ...current, ...settings };
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
};
